import { useCallback, useRef } from "react";

/**
 * Options for the useChatActions hook
 */
interface UseChatActionsOptions {
  /** The aiImportId for chat context */
  aiImportId: number | undefined;
  /** Function to scroll to bottom */
  scrollToBottom: () => void;
  /** Function to set pending question with push-up behavior */
  setPendingQuestionWithPushUp: (_question: string) => void;
  /** Shared send function from useChatSSE hook */
  send: (_params: SendParams) => Promise<void>;
}

/**
 * Return type for the useChatActions hook
 */
interface UseChatActionsReturn {
  /** Handler for refreshing/resending the last message */
  handleRefresh: () => void;
  /** Handler for resending a specific message */
  handleResend: (_resendText: string) => void;
  /** Handler for when a new question is sent */
  handleQuestionSent: (_question: string) => void;
  /** Ref to store the last user message for refresh functionality */
  lastUserMessageRef: React.MutableRefObject<string>;
}

/**
 * Custom hook for managing chat actions (resend, refresh, question sent).
 * Handles all user interaction callbacks and message state.
 *
 * @param options - Configuration options including aiImportId and scroll utilities
 * @returns Action handlers and message ref
 *
 * @example
 * const { handleRefresh, handleResend, handleQuestionSent } = useChatActions({
 *   aiImportId: 123,
 *   scrollToBottom: () => {}
 * });
 */
export function useChatActions({
  aiImportId,
  scrollToBottom,
  setPendingQuestionWithPushUp,
  send,
}: UseChatActionsOptions): UseChatActionsReturn {

  // Component refs for managing focus and scroll behavior
  const lastUserMessageRef = useRef<string>(""); // Stores last user message for resend functionality

  /**
   * Memoized callback for refreshing/resending messages.
   * Used in multiple places to avoid creating new functions on each render.
   */
  const handleRefresh = useCallback(() => {
    const messageText = lastUserMessageRef.current;
    if (messageText) {
      // Set pending question, push it up, and send
      setPendingQuestionWithPushUp(messageText);
      send({ question: messageText, aiImportId: aiImportId ?? undefined });
      scrollToBottom();
    }
  }, [send, aiImportId, setPendingQuestionWithPushUp, scrollToBottom]);

  /**
   * Memoized callback for resending messages.
   * Used for both DB and store message resending.
   */
  const handleResend = useCallback(
    (resendText: string) => {
      if (!resendText) return;
      // Set pending question, push it up, and send
      setPendingQuestionWithPushUp(resendText);
      lastUserMessageRef.current = resendText;
      send({ question: resendText, aiImportId: aiImportId ?? undefined });
    },
    [send, aiImportId, setPendingQuestionWithPushUp]
  );

  /**
   * Memoized callback for when a new question is sent.
   * Sets up the UI state to show the new question at the top.
   */
  const handleQuestionSent = useCallback(
    (question: string) => {
      setPendingQuestionWithPushUp(question);
      // Capture the last user message so refresh works on live response
      lastUserMessageRef.current = question;
    },
    [setPendingQuestionWithPushUp]
  );

  return {
    handleRefresh,
    handleResend,
    handleQuestionSent,
    lastUserMessageRef,
  };
}
